/**
 * Swedish mobile helpers. Accepts national format (07XXXXXXXX) or E.164 (+467XXXXXXXX).
 * E.164 is the ITU international numbering standard — country code + subscriber number, no separators.
 * @see https://en.wikipedia.org/wiki/E.164
 */
const SE_NATIONAL_MOBILE = {
  prefix: '07',
  regex: /^07\d{8}$/,
  maxLength: 10,
} as const;

const SE_E164_MOBILE = {
  prefix: '+46',
  regex: /^\+467\d{8}$/,
  maxLength: 12,
} as const;

const stripMobileSeparators = (mobile: string) => mobile.replace(/[ -]/g, '');

const isValidSeMobileInput = (normalized: string) =>
  SE_NATIONAL_MOBILE.regex.test(normalized) || SE_E164_MOBILE.regex.test(normalized);

export const isValidSeMobile = (mobile: string) => isValidSeMobileInput(stripMobileSeparators(mobile));

const getSeMobileMaxLength = (normalized: string) =>
  normalized.startsWith(SE_E164_MOBILE.prefix) ? SE_E164_MOBILE.maxLength : SE_NATIONAL_MOBILE.maxLength;

type SeMobileValidationMessages = {
  invalid: string;
  tooLong: (maxLength: number) => string;
};

export const validateSeMobileField = (mobile: string, messages: SeMobileValidationMessages) => {
  const normalized = stripMobileSeparators(mobile);

  if (normalized.length > getSeMobileMaxLength(normalized)) {
    return messages.tooLong(getSeMobileMaxLength(normalized));
  }

  return isValidSeMobileInput(normalized) || messages.invalid;
};

export const toNationalSeMobile = (mobile: string) => {
  const normalized = stripMobileSeparators(mobile);

  if (SE_NATIONAL_MOBILE.regex.test(normalized)) {
    return normalized;
  }

  if (SE_E164_MOBILE.regex.test(normalized)) {
    return `0${normalized.slice(SE_E164_MOBILE.prefix.length)}`;
  }

  return mobile;
};

export const toE164SeMobile = (mobile: string) => {
  const national = stripMobileSeparators(toNationalSeMobile(mobile));

  if (SE_NATIONAL_MOBILE.regex.test(national)) {
    return `${SE_E164_MOBILE.prefix}${national.slice(1)}`;
  }

  return mobile;
};

export const resolveMobileFormValue = (mobile?: string) =>
  mobile ? toNationalSeMobile(mobile) : SE_NATIONAL_MOBILE.prefix;
